/**
 * RoleSelector Component
 *
 * Custom dropdown for selecting workspace roles using Mantine Menu.
 * Displays role icon with color coding, label, and selection checkmark.
 * Reusable across invitation forms and member management.
 *
 * @module components/common/RoleSelector
 */

import { memo, useCallback, useState } from 'react';
import { Menu } from '@mantine/core';
import { IconChevronDown, IconCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { ROLE_CONFIG } from '@/constants/roles';
import { WORKSPACE_ROLES } from '@/types/workspace';
import { ICON_SIZE_XS, ICON_SIZE_SM } from '@/constants/ui';
import type { WorkspaceRole } from '@/types/workspace';
import styles from './RoleSelector.module.css';

// ===== CONSTANTS =====

/** Icon sizes for each trigger size variant */
const ROLE_ICON_SIZE_SM = 14;
const ROLE_ICON_SIZE_MD = 16;

/** Chevron icon size */
const CHEVRON_ICON_SIZE = 14;

/** Selected check icon size */
const CHECK_ICON_SIZE = ICON_SIZE_XS;

/** Menu item role icon size */
const MENU_ITEM_ICON_SIZE = ICON_SIZE_SM;

/** Badge variant icon size */
const BADGE_ICON_SIZE = 10;

// ===== TYPES =====

export interface RoleSelectorProps {
  /** Currently selected role */
  readonly value: WorkspaceRole;
  /** Callback when role changes */
  readonly onChange: (role: WorkspaceRole) => void;
  /** Roles available for selection (defaults to all roles) */
  readonly availableRoles?: readonly WorkspaceRole[];
  /** Whether the selector is disabled */
  readonly disabled?: boolean;
  /** Size variant - sm for list items, md for input rows */
  readonly size?: 'sm' | 'md';
  /** Visual variant - button (bordered) or badge (colored pill) */
  readonly variant?: 'button' | 'badge';
  /** Additional CSS class for the trigger */
  readonly className?: string;
}

// ===== ROLE STYLE MAPPING =====

/** Maps each role to its CSS module class for background and icon color.
 * Assertion needed because CSS module imports are typed as `string | undefined`. */
const ROLE_STYLE_MAP = {
  Admin: styles.roleAdmin,
  Editor: styles.roleEditor,
  Viewer: styles.roleViewer,
} as Record<WorkspaceRole, string>;

// ===== HELPER COMPONENTS =====

interface RoleIconBoxProps {
  readonly role: WorkspaceRole;
  readonly sizeClass: string;
  readonly iconSize: number;
}

/** Renders the colored icon box for a role */
function RoleIconBox({ role, sizeClass, iconSize }: RoleIconBoxProps) {
  const config = ROLE_CONFIG[role];
  const IconComponent = config.icon;
  const roleClass = ROLE_STYLE_MAP[role];

  return (
    <span className={`${styles.roleIconBox} ${sizeClass} ${roleClass}`}>
      <IconComponent size={iconSize} />
    </span>
  );
}

// ===== MAIN COMPONENT =====

export const RoleSelector = memo(function RoleSelector({
  value,
  onChange,
  availableRoles = WORKSPACE_ROLES,
  disabled = false,
  size = 'md',
  variant = 'button',
  className,
}: RoleSelectorProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const handleRoleSelect = useCallback(
    (role: WorkspaceRole) => {
      if (role !== value) {
        onChange(role);
      }
    },
    [value, onChange]
  );

  const isBadge = variant === 'badge';
  const isSizeSmall = size === 'sm';
  const selectedLabel = t(`workspace.roles.${value.toLowerCase()}`);
  const roleStyleClass = ROLE_STYLE_MAP[value] ?? '';
  const chevronClassName =
    `${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`.trim();
  const disabledClass = disabled ? styles.triggerDisabled : '';

  const ariaProps = {
    'aria-label': t('workspace.roles.selectRole', { role: selectedLabel }),
    'aria-expanded': isOpen,
    'aria-haspopup': 'listbox' as const,
  };

  const BadgeIcon = ROLE_CONFIG[value].icon;

  const triggerSizeClass = isSizeSmall ? styles.triggerSm : styles.triggerMd;
  const iconBoxSizeClass = isSizeSmall
    ? (styles.roleIconBoxSm ?? '')
    : (styles.roleIconBoxMd ?? '');
  const labelSizeClass = isSizeSmall
    ? styles.triggerLabelSm
    : styles.triggerLabelMd;
  const triggerIconSize = isSizeSmall ? ROLE_ICON_SIZE_SM : ROLE_ICON_SIZE_MD;

  const triggerElement = isBadge ? (
    <button
      type="button"
      className={`${styles.triggerBadge} ${roleStyleClass} ${disabledClass} ${className ?? ''}`.trim()}
      disabled={disabled}
      {...ariaProps}
    >
      <BadgeIcon size={BADGE_ICON_SIZE} className={styles.badgeIcon} />
      <span>{selectedLabel}</span>
      <IconChevronDown size={CHEVRON_ICON_SIZE} className={chevronClassName} />
    </button>
  ) : (
    <button
      type="button"
      className={`${styles.trigger} ${triggerSizeClass} ${disabledClass} ${className ?? ''}`.trim()}
      disabled={disabled}
      {...ariaProps}
    >
      <RoleIconBox
        role={value}
        sizeClass={iconBoxSizeClass}
        iconSize={triggerIconSize}
      />
      <span className={`${styles.triggerLabel} ${labelSizeClass}`}>
        {selectedLabel}
      </span>
      <IconChevronDown size={CHEVRON_ICON_SIZE} className={chevronClassName} />
    </button>
  );

  return (
    <Menu
      shadow="md"
      position="bottom-start"
      offset={4}
      opened={isOpen}
      onChange={setIsOpen}
      classNames={{ dropdown: styles.dropdown }}
    >
      <Menu.Target>{triggerElement}</Menu.Target>

      <Menu.Dropdown>
        {availableRoles.map((role) => {
          const isSelected = role === value;
          const roleLabel = t(`workspace.roles.${role.toLowerCase()}`);

          return (
            <Menu.Item
              key={role}
              className={clsx(
                styles.menuItem,
                isSelected && styles.activeMenuItem
              )}
              onClick={() => handleRoleSelect(role)}
            >
              <div className={styles.menuItemContent}>
                <RoleIconBox
                  role={role}
                  sizeClass={styles.roleIconBoxMd ?? ''}
                  iconSize={MENU_ITEM_ICON_SIZE}
                />
                <span className={styles.menuItemLabel}>{roleLabel}</span>
                {isSelected && (
                  <IconCheck
                    size={CHECK_ICON_SIZE}
                    className={styles.checkIcon}
                  />
                )}
              </div>
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
});
